import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useLang } from "@/i18n";
import { PageHero } from "@/components/common";
import { NftCard } from "@/components/NftCard";
import { NFTS, RARITY_ORDER } from "@/data/nfts";

const FILTERS = ["all", "genesis", "gold", "legendary", "collectibles"];
const SORTS = ["newest", "name", "rarity", "collection"];

export default function NftGallery() {
  const { t } = useLang();
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [query, setQuery] = useState("");

  const list = useMemo(() => {
    let out = [...NFTS];
    if (filter !== "all") {
      out = out.filter((n) =>
        filter === "legendary" ? n.rarity === "legendary" : n.collection === filter
      );
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter((n) => n.name.toLowerCase().includes(q) || n.id.toLowerCase().includes(q));
    }
    switch (sort) {
      case "name": out.sort((a, b) => a.name.localeCompare(b.name)); break;
      case "rarity": out.sort((a, b) => (RARITY_ORDER[b.rarity] || 0) - (RARITY_ORDER[a.rarity] || 0)); break;
      case "collection": out.sort((a, b) => a.collection.localeCompare(b.collection)); break;
      default: break;
    }
    return out;
  }, [filter, sort, query]);

  return (
    <>
      <PageHero eyebrow={t("nft.eyebrow")} title={t("gallery.title")} sub={t("gallery.note")} />

      <section className="section-pad" data-testid="nft-gallery">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={filter === f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                className={`mono-chip transition-colors ${filter === f ? "border-gold/60 bg-gold/10 text-goldlight" : "hover:border-white/30"}`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-3">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="sort-select"
              className="rounded-lg border border-white/10 bg-panel px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-mist focus:border-neon/50 focus:outline-none"
            >
              {SORTS.map((s) => (
                <option key={s} value={s}>{t(`gallery.sort${s[0].toUpperCase() + s.slice(1)}`)}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 rounded-lg border border-white/10 bg-panel px-3 py-2">
              <Search size={14} className="text-mist" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("gallery.search")}
                data-testid="gallery-search"
                className="w-36 bg-transparent text-sm text-paper placeholder:text-mist/50 focus:outline-none"
              />
            </label>
          </div>
        </div>

        {list.length === 0 ? (
          <div className="mt-16 rounded-2xl border border-dashed border-white/10 p-16 text-center text-sm text-mist" data-testid="gallery-empty">
            {t("gallery.empty")}
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="gallery-grid">
            {list.map((nft, i) => <NftCard key={nft.slug} nft={nft} index={i} />)}
          </div>
        )}
      </section>
    </>
  );
}
